import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { DurationUnit, ExpiryRuleType, MicrobiologicalLimitation, StabilityRuleSource, StabilityStage, } from '../../database/schema.js';
import { pickActorFromUserActions, pickTimestampFromUserActions } from '../../utils.js';
import { Status } from '../../util/schemas.js';
import { zql } from '../../zero-schema.gen.js';
import { itemsDetail, valueItem } from '../../util/event-log-items.js';
import { EventLogResources, addEventLog } from '../util.js';
async function findContainerMaterialName(tx, id) {
    if (!id)
        return undefined;
    return (await tx.run(zql.containerMaterials.where('id', id).one()))?.name;
}
export const AddStabilityRuleSchema = z.object({
    stabilityStage: StabilityStage.nonoptional(),
    startEventNotes: z.string().optional(),
    carrierDrugId: z.uuidv7().optional(),
    additionalActiveSubstanceDrugId: z.uuidv7().optional(),
    storageLabelId: z.uuidv7().nonoptional(),
    tempMinC: z.number().optional(),
    tempMaxC: z.number().optional(),
    lightProtectionRequired: z.boolean(),
    moistureProtectionRequired: z.boolean(),
    doNotRefrigerate: z.boolean(),
    doNotFreeze: z.boolean(),
    controlledTemperature: z.boolean(),
    keepInOriginalPackaging: z.boolean(),
    durationValue: z.number().min(0.1).nonoptional(),
    durationUnit: DurationUnit.nonoptional(),
    expiryRuleType: ExpiryRuleType.nonoptional(),
    microbiologicalLimitation: MicrobiologicalLimitation.optional(),
    discardRemainderRequired: z.boolean(),
    containerTypeId: z.uuidv7().optional(),
    containerMaterialsIds: z.array(z.uuidv7()).optional(),
    singleDoseOnly: z.boolean(),
    preservativePresent: z.boolean(),
    requiresAsepticHandling: z.boolean(),
    compatibilityNotes: z.string().optional(),
    notes: z.string().optional(),
    // preparation labels to print; omitted = leave unchanged, null = clear (fall back to the org default)
    labelCopies: z.int().min(1).nullable().optional(),
    defaultRule: z.boolean(),
    id: z.uuidv7().optional(), // used to differentiate between already existing items and new items
});
export const UpdateStabilityRuleSchema = AddStabilityRuleSchema;
// #region CREATE
/**
 * Creates a stability rule and an event log.
 */
export async function createStabilityRule(tx, args, userActions, 
/** drug or trade name the rule belongs to, shown in its logs */
itemName) {
    const newStabilityRule = {
        id: uuidv7(),
        carrierDrugId: args.carrierDrugId,
        additionalActiveSubstanceDrugId: args.additionalActiveSubstanceDrugId,
        durationUnit: args.durationUnit,
        durationValue: args.durationValue,
        expiryRuleType: args.expiryRuleType,
        stabilityStage: args.stabilityStage,
        compatibilityNotes: args.compatibilityNotes,
        containerTypeId: args.containerTypeId,
        controlledTemperature: args.controlledTemperature,
        discardRemainderRequired: args.discardRemainderRequired,
        doNotFreeze: args.doNotFreeze,
        doNotRefrigerate: args.doNotRefrigerate,
        isDefaultStage: args.defaultRule,
        keepInOriginalPackaging: args.keepInOriginalPackaging,
        lightProtectionRequired: args.lightProtectionRequired,
        microbiologicalLimitation: args.microbiologicalLimitation,
        moistureProtectionRequired: args.moistureProtectionRequired,
        notes: args.notes,
        labelCopies: args.labelCopies ?? null,
        preservativePresent: args.preservativePresent,
        requiresAsepticHandling: args.requiresAsepticHandling,
        singleDoseOnly: args.singleDoseOnly,
        startEventNotes: args.startEventNotes,
        status: Status.enum.active,
        storageLabelId: args.storageLabelId,
        tempMaxC: args.tempMaxC,
        tempMinC: args.tempMinC,
        source: args.source,
        ...userActions,
    };
    const promises = [tx.mutate.stabilityRules.insert(newStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.stabilityRule,
            resourceId: newStabilityRule.id,
            action: 'create',
            patches: patches,
            performedBy: pickActorFromUserActions(userActions),
            performedAt: pickTimestampFromUserActions(userActions),
            details: { itemName },
        }));
    }
    await Promise.all(promises);
    if (args.containerMaterialsIds?.length) {
        await Promise.all(args.containerMaterialsIds.map(containerMaterialId => createStabilityRuleContainerMaterialRelation(tx, {
            containerMaterialId,
            stabilityRuleId: newStabilityRule.id,
        }, userActions, itemName)));
    }
    return newStabilityRule;
}
/**
 * Creates a stability rule - container material relation (used to define what materials can be used for a stability rule) and an event log.
 */
async function createStabilityRuleContainerMaterialRelation(tx, args, userActions, itemName) {
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const newMaterial = {
        id: uuidv7(),
        containerMaterialId: args.containerMaterialId,
        stabilityRuleId: args.stabilityRuleId,
        createdAt: timestamp,
        createdBy: actor,
    };
    const promises = [tx.mutate.stabilityRuleContainerMaterials.insert(newMaterial)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newMaterial);
        const material = await findContainerMaterialName(tx, args.containerMaterialId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.stabilityRuleContainerMaterial,
            resourceId: newMaterial.id,
            action: 'create',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            details: { itemName, ...itemsDetail(valueItem('material', material)) },
        }));
    }
    await Promise.all(promises);
    return newMaterial;
}
// #endregion
// #region UPDATE
export async function updateStabilityRule(tx, args, userActions, itemName) {
    if (!args.id)
        return null;
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const prev = await tx.run(zql.stabilityRules.where('id', args.id).where('deletedAt', 'IS', null).one());
    const updatedStabilityRule = {
        id: args.id,
        carrierDrugId: args.carrierDrugId,
        additionalActiveSubstanceDrugId: args.additionalActiveSubstanceDrugId,
        durationUnit: args.durationUnit,
        durationValue: args.durationValue,
        expiryRuleType: args.expiryRuleType,
        stabilityStage: args.stabilityStage,
        compatibilityNotes: args.compatibilityNotes,
        containerTypeId: args.containerTypeId,
        controlledTemperature: args.controlledTemperature,
        discardRemainderRequired: args.discardRemainderRequired,
        doNotFreeze: args.doNotFreeze,
        doNotRefrigerate: args.doNotRefrigerate,
        isDefaultStage: args.defaultRule,
        keepInOriginalPackaging: args.keepInOriginalPackaging,
        lightProtectionRequired: args.lightProtectionRequired,
        microbiologicalLimitation: args.microbiologicalLimitation,
        moistureProtectionRequired: args.moistureProtectionRequired,
        notes: args.notes,
        labelCopies: args.labelCopies ?? null,
        preservativePresent: args.preservativePresent,
        requiresAsepticHandling: args.requiresAsepticHandling,
        singleDoseOnly: args.singleDoseOnly,
        startEventNotes: args.startEventNotes,
        status: Status.enum.active,
        storageLabelId: args.storageLabelId,
        tempMaxC: args.tempMaxC,
        tempMinC: args.tempMinC,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.stabilityRules.update(updatedStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch(prev, updatedStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.stabilityRule,
            resourceId: updatedStabilityRule.id,
            action: 'update',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            details: { itemName },
        }));
    }
    await Promise.all(promises);
    const prevStabilityRule = await tx.run(zql.stabilityRules.related('containerMaterials').where('id', args.id).one());
    if (prevStabilityRule && args.containerMaterialsIds?.length) {
        await replaceStabilityRuleContainerMaterialsRelations(tx, {
            stabilityRule: prevStabilityRule,
            containerMaterialsIds: args.containerMaterialsIds,
        }, userActions, itemName);
    }
    return updatedStabilityRule;
}
// #endregion
// #region DELETE
export async function deleteStabilityRule(tx, stabilityRuleId, userActions, itemName) {
    const stabilityRule = await tx.run(zql.stabilityRules.related('containerMaterials').where('id', stabilityRuleId).one());
    if (!stabilityRule)
        return;
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const deletedStabilityRule = {
        id: stabilityRuleId,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.stabilityRules.update(deletedStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch(stabilityRule, deletedStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.stabilityRule,
            resourceId: deletedStabilityRule.id,
            action: 'delete',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            details: { itemName },
        }));
    }
    await Promise.all(promises);
    if (stabilityRule.containerMaterials) {
        await Promise.all(stabilityRule.containerMaterials.map(stabilityRuleContainerMaterial => deleteStabilityRuleContainerMaterialRelation(tx, { stabilityRuleContainerMaterialId: stabilityRuleContainerMaterial.id }, userActions, itemName)));
    }
    return deletedStabilityRule;
}
export async function deleteStabilityRuleContainerMaterialRelation(tx, args, userActions, itemName) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const deletedMaterial = {
        id: args.stabilityRuleContainerMaterialId,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.stabilityRuleContainerMaterials.update(deletedMaterial)];
    if (tx.location === 'server') {
        const patches = createPatch({}, deletedMaterial);
        const relation = await tx.run(zql.stabilityRuleContainerMaterials.where('id', deletedMaterial.id).one());
        const material = await findContainerMaterialName(tx, relation?.containerMaterialId);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.stabilityRuleContainerMaterial,
            resourceId: deletedMaterial.id,
            action: 'delete',
            patches: patches,
            performedBy: actor,
            performedAt: timestamp,
            details: { itemName, ...itemsDetail(valueItem('material', material)) },
        }));
    }
    await Promise.all(promises);
    return deletedMaterial;
}
// #endregion
// #region REPLACE
export async function replaceStabilityRuleContainerMaterialsRelations(tx, { stabilityRule, containerMaterialsIds }, userActions, itemName) {
    const prevRuleContainerMaterialsIds = new Set(stabilityRule.containerMaterials?.map(ruleContainerMaterial => ruleContainerMaterial.containerMaterialId));
    const newContainerMaterialsIds = new Set(containerMaterialsIds);
    const addedMaterials = containerMaterialsIds?.filter(id => !prevRuleContainerMaterialsIds.has(id)) || [];
    const deletedMaterials = stabilityRule?.containerMaterials?.filter(item => !newContainerMaterialsIds.has(item.containerMaterialId)) || [];
    if (addedMaterials.length) {
        await Promise.all(addedMaterials.map(materialId => createStabilityRuleContainerMaterialRelation(tx, {
            containerMaterialId: materialId,
            stabilityRuleId: stabilityRule.id,
        }, userActions, itemName)));
    }
    if (deletedMaterials.length) {
        await Promise.all(deletedMaterials.map(stabilityRuleMaterial => deleteStabilityRuleContainerMaterialRelation(tx, { stabilityRuleContainerMaterialId: stabilityRuleMaterial.id }, userActions, itemName)));
    }
}
// #endregion
/**
 * Preparation labels to print: the highest value set on any applicable stability rule, or the
 * organisation default when no rule sets one. Rules that leave it unset don't affect the result.
 */
export function resolveStabilityRuleLabelCopies(rules, defaultCopies) {
    const counts = rules.map(rule => rule.labelCopies).filter((count) => count != null);
    return counts.length ? Math.max(...counts) : defaultCopies;
}
//# sourceMappingURL=stability-rules.js.map